/** Tag → Data tab. The schema-driven editor for the tag's `data` blob, built
 *  from the category's effective schema (embedded in the tag detail).
 *
 *  "No schema" is not a dead end: the alert links to the category's General
 *  tab, which is where a schema is actually attached.
 *
 *  Translation bindings (backend metaplan A6): auto-translated leaves are marked, and a save
 *  that changes one asks first whether to unbind it (the default) or keep the binding. */
import { Alert, Box, Link as MuiLink } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagsApi from '@/api/tags';
import type { TagDetail, TranslationBinding } from '@/api/tags';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { TagDataEditor } from '../data/TagDataEditor';
import { bindingsByAttribute, changedBoundTargets } from '../data/translationBindings';
import { UnbindTranslationsDialog } from '../data/UnbindTranslationsDialog';

export function TagDataTab({ tag, onSaved }: { tag: TagDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tags_manage');
  const snackbar = useSnackbar();

  const baseline = useMemo<Record<string, unknown>>(() => tag.data ?? {}, [tag.data]);
  const [draft, setDraft] = useState<Record<string, unknown>>(baseline);
  useEffect(() => setDraft(baseline), [baseline]);
  const bindings = useMemo(() => bindingsByAttribute(tag.translation_bindings), [tag.translation_bindings]);
  const [asking, setAsking] = useState<TranslationBinding[] | null>(null);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);

  const patch = useMutation({
    mutationFn: (unbind: number[]) =>
      tagsApi.update(tag.id, unbind.length ? { data: draft, translation_unbind: unbind } : { data: draft }),
    onSuccess: (_detail, unbind) => {
      setAsking(null);
      snackbar.show(unbind.length ? `Data saved; ${unbind.length} leaf/leaves unbound.` : 'Data saved.', 'success');
      onSaved();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const save = () => {
    const changed = changedBoundTargets(tag.translation_bindings, baseline, draft);
    if (changed.length) setAsking(changed);
    else patch.mutate([]);
  };

  return (
    <Box>
      {!tag.category?.schema && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This tag&apos;s category has no schema — all fields show under &ldquo;Additional
          fields&rdquo;. Add a schema to{' '}
          <MuiLink component={RouterLink} to={`/tag-categories/${tag.category.id}/general`}>
            {tag.category.name}
          </MuiLink>{' '}
          for a guided editor.
        </Alert>
      )}
      <TagDataEditor
        schema={tag.category?.schema ?? null}
        value={draft}
        onChange={setDraft}
        readOnly={!canManage}
        translationBindings={bindings}
      />
      <Box sx={{ mt: 2 }}>
        <EditorSaveBar
          dirty={dirty} saving={patch.isPending} canManage={canManage}
          onSave={save} onDiscard={() => setDraft(baseline)}
        />
      </Box>
      <UnbindTranslationsDialog
        open={asking !== null}
        bindings={asking ?? []}
        saving={patch.isPending}
        onCancel={() => setAsking(null)}
        onConfirm={(unbind) => patch.mutate(unbind)}
      />
    </Box>
  );
}
